'use client';

import { useAnalyticsSkuViewedContext } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { createHandleOnClick, getFinishType } from '@ria-client/segment/Analytics/utils';
import { MonetizableEnum } from '@ria-client/segment/enums';
import { useGetCtaLink } from '@ria-client/segment/hooks/useGetCtaLink';
import { EntityPlacementCtaTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import { cn } from 'cn';
import type { TrackedLinkProps } from './TrackedLink';

interface Props extends TrackedLinkProps {
  /** Minted by TrackedLink so the resolved URL and the event agree. */
  shoppingFinishedId: string;
}

/**
 * The tracked anchor. Mirrors packages/segment/src/components/CtaLink/ClientCtaLink.tsx
 * with the MUI Link swapped for a native anchor, and the `Component` polymorphism
 * dropped in favour of composition — a caller that wants a button wraps this in
 * the shadcn Button with `asChild`.
 */
export function ClientTrackedLink({
  analyticsPayload,
  cta,
  children,
  linkType,
  monetizable,
  shoppingFinishedId,
  ...rest
}: Props): React.JSX.Element {
  const { index } = analyticsPayload;
  const { fireShoppingFinishedEvent } = useAnalyticsSkuViewedContext();
  const ctaUrl = useGetCtaLink({
    href: cta.link,
    type: linkType,
    monetizable,
    elementPosition: index,
    shoppingFinishedId,
  });

  const finishType = getFinishType(monetizable);

  /*
   * A monetizing clickout fires Shopping Finished at the partner redirect, which
   * is why only the non-monetizing branch emits here. Firing both would
   * double-count revenue attribution.
   */
  const handleOnClick = () => {
    if (monetizable === MonetizableEnum.no) {
      createHandleOnClick({
        fireShoppingFinishedEvent,
        overrideShoppingFinishedId: shoppingFinishedId,
        destinationUrl: ctaUrl,
        monetizable,
        type: cta.type,
      })();
    }
  };

  const { className, ...anchorProps } = rest;
  const isPrequal = cta.type === EntityPlacementCtaTypeEnum.Prequal;

  /*
   * A native anchor, not the tw-shared-ui layout Link. That Link forces
   * rel="noopener noreferrer" / target="_blank" on every absolute URL and omits
   * rel/target from its prop type, so it cannot express the CTA contract:
   * rel="nofollow" for external applications, target="_self" only for Prequal.
   * _blank still gets noopener noreferrer (tabnabbing); Prequal (_self) emits
   * no rel. focus-visible:focus-ring keeps the shared RIA focus treatment.
   *
   * CTA tracking and link attributes deliberately are not caller-overridable, so
   * onClick/rel/target come after the spread.
   */
  return (
    <a
      data-slot="link"
      href={ctaUrl}
      {...anchorProps}
      className={cn('outline-none focus-visible:focus-ring', className)}
      onClick={handleOnClick}
      rel={
        finishType === 'external_application'
          ? 'nofollow noopener noreferrer'
          : isPrequal
            ? undefined
            : 'noopener noreferrer'
      }
      target={isPrequal ? '_self' : '_blank'}
    >
      {children}
    </a>
  );
}
